import queryClient from "@src/constants/queryClient"
import { trackActivity } from "@src/features/activity/actions"
import type { ActivityInput } from "@src/features/activity/schema"
import type { UnifiedFeedResponse } from "@src/features/activity/types"
import { BASE_URL } from "@src/utils/environment"
import { logger } from "@src/utils/logger"

export const UNIFIED_FEED_QUERY_KEY = "unified_feed"

const REQUEST_TIMEOUT_MS = 30_000

export type {
  FeedItem,
  UnifiedFeedResponse,
} from "@src/features/activity/types"

export type UnifiedFeedTypeFilter = "all" | "swap" | "receive" | "send"

export async function fetchUnifiedFeed(options: {
  type?: UnifiedFeedTypeFilter
  limit?: number
  cursor?: string | null
}): Promise<UnifiedFeedResponse> {
  const searchParams = new URLSearchParams()
  if (options.type && options.type !== "all") {
    searchParams.set("type", options.type)
  }
  if (options.limit) searchParams.set("limit", String(options.limit))
  if (options.cursor) searchParams.set("cursor", options.cursor)

  const queryString = searchParams.toString()
  const url = `${BASE_URL}/api/activities${queryString ? `?${queryString}` : ""}`

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)

  try {
    const response = await fetch(url, {
      signal: controller.signal,
      cache: "no-store",
    })
    clearTimeout(timeoutId)

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error ?? "Failed to fetch feed")
    }

    const json: UnifiedFeedResponse = await response.json()

    // Rehydrate activity dates
    return {
      ...json,
      data: json.data.map((item) => {
        if (item.source === "activity") {
          return {
            ...item,
            data: {
              ...item.data,
              createdAt: new Date(item.data.createdAt),
            },
          }
        }
        return item
      }),
    }
  } catch (error) {
    clearTimeout(timeoutId)
    if (error instanceof Error && error.name === "AbortError") {
      throw new Error("Request timed out")
    }
    throw error
  }
}

export function trackAndRefreshFeed(input: ActivityInput): void {
  trackActivity(input)
    .then(() =>
      queryClient.invalidateQueries({ queryKey: [UNIFIED_FEED_QUERY_KEY] })
    )
    .catch((err) => logger.warn("Failed to track activity", { cause: err }))
}
